import { isObject } from '../../common';
import { type FieldValue } from './adaptor';

/**
 * Duck-type check for Firestore FieldValue sentinels (delete, serverTimestamp,
 * increment, arrayUnion, arrayRemove). These must pass through serialisation
 * unchanged so the Firestore SDK can interpret them at write time.
 *
 * Sentinels have no own enumerable properties, so spreading them produces an
 * empty object. This guard prevents the serialiser from destroying them.
 *
 * The sentinel shape differs across SDK versions:
 * - firebase-admin (node): has `includeInDocumentTransform` (method in <v12,
 *   boolean in v12+) and `isEqual`.
 * - firebase/firestore (browser modular SDK): has `_toFieldTransform` method
 *   and `isEqual` but no `includeInDocumentTransform`.
 *
 * We accept all forms so the same guard works in both environments.
 */
export function isFieldValue(value: unknown): value is FieldValue {
  if (
    !isObject(value) ||
    !('isEqual' in value) ||
    typeof value.isEqual !== 'function'
  ) {
    return false;
  }

  // Node admin SDK sentinel (method in older versions, boolean in v12+)
  if (
    'includeInDocumentTransform' in value &&
    (typeof value.includeInDocumentTransform === 'function' ||
      typeof value.includeInDocumentTransform === 'boolean')
  ) {
    return true;
  }

  // Browser modular SDK sentinel
  if (
    '_toFieldTransform' in value &&
    typeof value._toFieldTransform === 'function'
  ) {
    return true;
  }

  return false;
}

/**
 * Like Partial<T> but allows FieldValue sentinels (e.g. FieldValue.delete(),
 * FieldValue.increment()) at any property position. This mirrors the
 * PartialWithFieldValue type from @google-cloud/firestore but is defined here
 * so it works in both node and browser environments.
 */
export type PartialWithFieldValue<T> = T extends Primitive
  ? T
  : T extends Map<infer K, infer V>
    ? Map<K, V | FieldValue>
    : T extends (infer U)[]
      ? PartialWithFieldValue<U>[]
      : { [K in keyof T]?: T[K] | FieldValue | PartialWithFieldValue<T[K]> };

type Primitive = string | number | boolean | undefined | null;
